/**
 * Email Reply Form Controller — TinyMCE lifecycle for the quick reply/forward form.
 *
 * Key robustness behaviours:
 *   - double-rAF to ensure DOM is ready before TinyMCE init
 *   - stale editor detection on init
 *   - guaranteed cleanup on disconnect (even after Turbo Frame replacements)
 *   - auto-saves editor content to textarea on change/input/keyup
 *   - on mobile: opens a <dialog> (browser top layer — above all z-index and
 *     overflow:hidden) and moves the expanded form into it; closes on cancel/send
 */

import { Controller } from '@hotwired/stimulus'
import { I18n } from 'i18n-js'
import locales from '../locales.json'
import { initTinyMCE, removeTinyMCE } from '../modules/email/email-tinymce.js'

export default class extends Controller {
  static targets  = ['editor']
  static values   = { expanded: Boolean }

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'
    this._dialogOriginalParent      = null
    this._dialogOriginalNextSibling = null

    if (this.expandedValue) {
      this._openDialog()
      this.initTinyMCE()
    }
  }

  disconnect() {
    this._closeDialogAndRestore()
    if (this.hasEditorTarget) {
      removeTinyMCE(this.editorTarget)
    }
  }

  // ── Public actions ────────────────────────────────────────────────────────

  initTinyMCE() {
    if (!this.hasEditorTarget) return
    initTinyMCE(this.editorTarget, {
      onImageUploadError: () => this.i18n.t('emails.image_upload_failed')
    }).then(() => {
      if (window.innerWidth < 768) this._fitEditorToDialog()
    })
  }

  removeTinyMCE() {
    if (this.hasEditorTarget) {
      removeTinyMCE(this.editorTarget)
    }
  }

  syncBeforeSubmit() {
    window.tinymce?.get('email_quick_reply_editor')?.save()
  }

  close(e) {
    e?.preventDefault?.()
    this._closeDialogAndRestore()
    const trigger = document.getElementById('email_reply_trigger')
    if (trigger) trigger.classList.remove('d-none')
    window.tinymce?.remove('#email_quick_reply_editor')
  }

  // ── Dialog: move expanded form into <dialog id="email-reply-dialog"> ─────
  //
  // The <dialog> element is rendered into the browser's "top layer" via
  // showModal(). The top layer is always above all z-index stacking contexts
  // and is never clipped by overflow:hidden on any ancestor. This is the only
  // reliable cross-browser solution when all ancestors have overflow:hidden.

  _openDialog() {
    if (window.innerWidth >= 768) return

    const expanded = document.getElementById('email_reply_form_expanded')
    const dialog   = document.getElementById('email-reply-dialog')
    if (!expanded || !dialog) return

    // Remember where to put it back
    this._dialogOriginalParent      = expanded.parentElement
    this._dialogOriginalNextSibling = expanded.nextSibling

    // Wrap expanded form in a flex container inside the dialog
    let inner = dialog.querySelector('.email-reply-dialog-inner')
    if (!inner) {
      inner = document.createElement('div')
      inner.className = 'email-reply-dialog-inner'
      dialog.appendChild(inner)
    }
    inner.innerHTML = ''
    inner.appendChild(expanded)

    // Remove d-none so form is visible
    expanded.classList.remove('d-none')

    dialog.showModal()
  }

  _closeDialogAndRestore() {
    const dialog   = document.getElementById('email-reply-dialog')
    const expanded = document.getElementById('email_reply_form_expanded')
    if (!expanded || !this._dialogOriginalParent) return

    // Move expanded form back to original position
    if (this._dialogOriginalNextSibling) {
      this._dialogOriginalParent.insertBefore(expanded, this._dialogOriginalNextSibling)
    } else {
      this._dialogOriginalParent.appendChild(expanded)
    }

    expanded.classList.add('d-none')

    if (dialog?.open) dialog.close()

    // Clear the inner wrapper
    const inner = dialog?.querySelector('.email-reply-dialog-inner')
    if (inner) inner.innerHTML = ''

    this._dialogOriginalParent      = null
    this._dialogOriginalNextSibling = null
  }

  // ── Mobile: resize TinyMCE to fill the dialog ────────────────────────────

  _fitEditorToDialog() {
    const overlay = document.getElementById('email_reply_form_expanded')
    if (!overlay) return

    requestAnimationFrame(() => {
      const headerEl  = document.getElementById('email_reply_mode_label')
      const addressEl = overlay.querySelector('.email-reply-address-fields')
      const actionEl  = overlay.querySelector('.email-reply-actions-bar')

      const used = (headerEl?.offsetHeight  || 44)
                 + (addressEl?.offsetHeight || 120)
                 + (actionEl?.offsetHeight  || 50)
                 + 32  // dialog inner padding

      const targetH = Math.max(80, window.innerHeight - used)

      const tox = overlay.querySelector('.tox-tinymce')
      if (tox) tox.style.height = `${targetH}px`
    })
  }
}
